import {
  ColumnFiltersState,
  PaginationState,
  Row,
  RowModel,
} from '@tanstack/react-table'
import { message } from 'antd'
import { useGeckoAnalytics } from 'features/analytics'
import {
  DocumentTableColumns,
  DocumentTableRow,
  SemanticSearchColumns,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/DocumentTableColumns'
import { useDocumentSemanticSearch } from 'features/knowledgeDocumentViewer/hooks/useDocumentSemanticSearch'
import { SearchMode } from 'features/knowledgeDocumentViewer/types'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

export interface UseAdvancedSearchProps {
  advancedSearchTableData: DocumentTableRow[]
  siteId?: string
  initialGlobalFilter?: string
  initialColumnFilters?: ColumnFiltersState
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  onSelectDocument: (documentId: string) => void
  onClose?: () => void
  searchMode: SearchMode
  setSearchMode: React.Dispatch<React.SetStateAction<SearchMode>>
  searchInputValue: string
  setSearchInputValue: React.Dispatch<React.SetStateAction<string>>
  semanticQuery: string
  setSemanticQuery: React.Dispatch<React.SetStateAction<string>>
  columnFilters: ColumnFiltersState
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
}

const filterBySearchText = (
  data: DocumentTableRow[],
  searchText: string
): DocumentTableRow[] => {
  if (!searchText) return data

  const searchValue = searchText.toLowerCase()
  return data.filter((row) => {
    const searchableFields = [
      row.title,
      row.documentTitle,
      row.system,
      row.vendor,
      row.docType,
      row.drawing,
      row.revisionCode,
    ]
    return searchableFields.some((field) =>
      field?.toLowerCase().includes(searchValue)
    )
  })
}

export function useAdvancedSearch({
  advancedSearchTableData,
  siteId,
  initialGlobalFilter = '',
  setPagination,
  onSelectDocument,
  onClose,
  searchMode,
  setSearchMode,
  setSearchInputValue,
  semanticQuery,
  setSemanticQuery,
  setColumnFilters,
}: UseAdvancedSearchProps) {
  const [searchText, setSearchText] = useState<string>(initialGlobalFilter)
  const [feedbackSelectedIds, setFeedbackSelectedIds] = useState<Set<string>>(
    () => new Set()
  )

  const { trackEvent } = useGeckoAnalytics()

  const {
    searchResults: semanticSearchResults,
    isLoading: isSemanticLoading,
    search: executeSemanticSearch,
    clearResults: clearSemanticResults,
    config: semanticSearchConfig,
  } = useDocumentSemanticSearch(siteId ?? '', advancedSearchTableData)

  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchText(value)
        setPagination((prev) => ({ ...prev, pageIndex: 0 }))
      }, 500),
    [setPagination]
  )

  const filteredTableData = useMemo(
    () => filterBySearchText(advancedSearchTableData, searchText),
    [advancedSearchTableData, searchText]
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSearchInputValue(value)
    debouncedUpdateSearch(value)
  }

  const handleSemanticQueryChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSemanticQuery(e.target.value)
  }

  const handleSemanticSearch = () => {
    if (semanticQuery.trim()) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
      executeSemanticSearch(semanticQuery)
    }
  }

  const handleSemanticKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSemanticSearch()
    }
  }

  const handleFeedbackToggle = useCallback((id: string) => {
    setFeedbackSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleSubmitFeedback = useCallback(() => {
    const allResults = semanticSearchResults.map((doc) => ({
      document_id: doc.id,
      title: doc.title,
    }))

    const relevantDocuments = semanticSearchResults
      .map((doc, index) => ({ doc, rank: index + 1 }))
      .filter(({ doc }) => feedbackSelectedIds.has(doc.id))
      .map(({ doc, rank }) => ({
        document_id: doc.id,
        rank,
      }))

    trackEvent('Semantic Search Feedback Submitted', {
      query: semanticQuery,
      site_id: siteId,
      all_results: allResults,
      relevant_documents: relevantDocuments,
      config: semanticSearchConfig,
    })

    message.success('Feedback submitted successfully')
    setFeedbackSelectedIds(new Set())
  }, [
    semanticSearchResults,
    feedbackSelectedIds,
    trackEvent,
    semanticQuery,
    siteId,
    semanticSearchConfig,
  ])

  const handleSearchModeChange = (value: SearchMode) => {
    setSearchMode(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    if (value === 'keyword') {
      clearSemanticResults()
    }
  }

  const handleDocumentSelect = useCallback(
    (document: DocumentTableRow) => {
      onSelectDocument(document.id)
      onClose?.()
    },
    [onSelectDocument, onClose]
  )

  const handleRowClick = (row: Row<DocumentTableRow>) => {
    handleDocumentSelect(row.original)
  }

  const handleClose = () => {
    onClose?.()
  }

  const handleFilteredDataChange = useCallback(
    (
      filteredData: RowModel<DocumentTableRow>,
      _globalFilter: unknown,
      newColumnFilters: ColumnFiltersState
    ) => {
      setColumnFilters(newColumnFilters)
    },
    [setColumnFilters]
  )

  useEffect(() => {
    return () => {
      debouncedUpdateSearch.cancel()
    }
  }, [debouncedUpdateSearch])

  useEffect(() => {
    setSearchInputValue(initialGlobalFilter)
    setSearchText(initialGlobalFilter)
  }, [initialGlobalFilter, setSearchInputValue])

  useEffect(() => {
    setFeedbackSelectedIds(new Set())
  }, [semanticSearchResults])

  const tableColumns = useMemo(() => {
    if (searchMode === 'semantic') {
      return SemanticSearchColumns({
        feedbackSelectedIds,
        onFeedbackToggle: handleFeedbackToggle,
      })
    }
    return DocumentTableColumns()
  }, [searchMode, feedbackSelectedIds, handleFeedbackToggle])

  const tableData =
    searchMode === 'semantic' ? semanticSearchResults : filteredTableData

  const showSemanticSearchEmptyState =
    searchMode === 'semantic' &&
    !isSemanticLoading &&
    semanticSearchResults.length === 0 &&
    semanticQuery.trim() !== ''

  return {
    feedbackSelectedIds,
    isSemanticLoading,
    showSemanticSearchEmptyState,
    tableData,
    tableColumns,

    handleSearchChange,
    handleSemanticQueryChange,
    handleSemanticKeyDown,
    handleSemanticSearch,
    handleSearchModeChange,
    handleFeedbackToggle,
    handleSubmitFeedback,
    handleRowClick,
    handleDocumentSelect,
    handleFilteredDataChange,
    handleClose,
  }
}
